// FICHA DESCRITIVA DA ESTAÇÃO — padrão ANA (Anexo I).
//
// São ~60 campos. Duas decisões de forma, ambas para não virar um rolo infinito:
//
// 1. **Seções em accordion**, não empilhadas. Abre-se o que se vai preencher.
// 2. **Seções condicionais por tipo**: réguas, RNs, transbordamento, seção de
//    medição, margens e controle só existem em fluviométrica/linimétrica. Uma
//    pluviométrica não vê 20 campos em branco que nunca vai usar.
//
// O banco guarda tudo independentemente do tipo — quem esconde é esta tela.
// Assim uma estação que muda de tipo não perde o que já foi preenchido.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, Collapse, DatePicker, Empty, Form, Image, Input,
  InputNumber, Modal, Row, Select, Space, Spin, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import {
  DeleteOutlined, DownloadOutlined, FilePdfOutlined, PlusOutlined, SaveOutlined, UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from './shared';

const { Text, Title } = Typography;
const { TextArea } = Input;

// Toda casa decimal deste sistema passa por aqui: sem `decimalSeparator=","` o
// antd COME a vírgula digitada e "3,45" vira 345. Já mordeu várias vezes —
// `locale={ptBR}` não corrige, tem que ser na prop do InputNumber.
const numProps = { decimalSeparator: ',', style: { width: '100%' }, controls: false } as const;

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Categorias de foto do checklist da ANA. Rótulo curto na tela, letra no banco.
const CATEGORIAS_FOTO = [
  { value: 'a', label: 'a) Vista geral da estação' },
  { value: 'b', label: 'b) Réguas / sensor instalados' },
  { value: 'c', label: 'c) Seção de medição — margem esquerda' },
  { value: 'd', label: 'd) Seção de medição — margem direita' },
  { value: 'e', label: 'e) Montante da seção' },
  { value: 'f', label: 'f) Jusante da seção' },
  { value: 'g', label: 'g) Referências de nível (RNs)' },
];

const OPTS = (...v: string[]) => v.map(x => ({ value: x, label: x }));

// ── Tabela editável genérica (os 5 blocos que a ANA desenha como tabela) ─────
// Cardinalidade livre: a ficha da ANA tem "uma linha por lance de régua", "uma
// linha por RN"… espremer isso em colunas viraria reguas_1..N.
function TabelaJson({ valor, onChange, colunas, rotuloAdd, leitura }: {
  valor: any[];
  onChange: (v: any[]) => void;
  colunas: { campo: string; titulo: string; tipo?: 'texto' | 'num' | 'data' | 'select'; opcoes?: string[]; largura?: number }[];
  rotuloAdd: string;
  leitura: boolean;
}) {
  const linhas = Array.isArray(valor) ? valor : [];
  const set = (i: number, campo: string, v: any) =>
    onChange(linhas.map((l, k) => (k === i ? { ...l, [campo]: v } : l)));

  const cols: any[] = colunas.map(c => ({
    title: c.titulo, key: c.campo, width: c.largura,
    render: (_: any, _l: any, i: number) => {
      const v = linhas[i]?.[c.campo];
      if (leitura) return <Text>{c.tipo === 'data' && v ? dayjs(v).format('DD/MM/YYYY') : (v ?? '—')}</Text>;
      if (c.tipo === 'num') return <InputNumber {...numProps} size="small" value={v} onChange={x => set(i, c.campo, x)} />;
      if (c.tipo === 'data') return (
        <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY"
          value={v ? dayjs(v) : null} onChange={d => set(i, c.campo, d ? d.format('YYYY-MM-DD') : null)} />
      );
      if (c.tipo === 'select') return (
        <Select size="small" style={{ width: '100%' }} allowClear showSearch
          options={OPTS(...(c.opcoes || []))} value={v || undefined} onChange={x => set(i, c.campo, x)} />
      );
      return <Input size="small" value={v || ''} onChange={e => set(i, c.campo, e.target.value)} />;
    },
  }));
  if (!leitura) cols.push({
    title: '', key: 'x', width: 44,
    render: (_: any, _l: any, i: number) => (
      <Button size="small" type="text" danger icon={<DeleteOutlined />}
        onClick={() => onChange(linhas.filter((_, k) => k !== i))} />
    ),
  });

  return (
    <>
      <Table size="small" pagination={false} rowKey={(_, i) => String(i)}
        dataSource={linhas} columns={cols}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem linhas" /> }} />
      {!leitura && (
        <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8 }}
          onClick={() => onChange([...linhas, {}])}>{rotuloAdd}</Button>
      )}
    </>
  );
}

// ── Anexos: presigned PUT direto no S3, depois registro no JSONB ─────────────
function BlocoAnexos({ estacaoId, tipo, anexos, recarregar, leitura, aceita, titulo, dica, categorias }: {
  // ⚠️ `recarregar` recarrega SÓ a lista de anexos, nunca a ficha inteira.
  // Quando chamava `carregar()`, anexar uma foto fazia `form.setFieldsValue`
  // com o que está NO BANCO — e apagava, sem aviso, tudo que a pessoa tinha
  // digitado e ainda não salvo, inclusive as linhas das 5 tabelas.
  estacaoId: string; tipo: string; anexos: any[]; recarregar: () => void;
  leitura: boolean; aceita: string; titulo: string; dica?: string;
  categorias?: { value: string; label: string }[];
}) {
  const [subindo, setSubindo] = useState(false);
  // A categoria é escolhida ANTES de anexar: a ANA quer o registro fotográfico
  // rotulado (a=vista geral, c=margem esquerda…), e depois do upload ninguém
  // volta pra classificar. O próximo item livre já vem pré-selecionado.
  const [categoria, setCategoria] = useState<string | undefined>();
  const [urls, setUrls] = useState<Record<string, string>>({});
  const meus = (anexos || []).filter(a => a.tipo === tipo);

  // Presigned GET de 1 h para pré-visualizar. Buscado sob demanda para não
  // disparar N chamadas ao abrir a ficha inteira.
  useEffect(() => {
    let vivo = true;
    (async () => {
      for (const a of meus) {
        if (urls[a.s3_key]) continue;
        try {
          const r = await api('op_estacao_ficha_anexo_download', { s3_key: a.s3_key });
          if (vivo && r?.url) setUrls(u => ({ ...u, [a.s3_key]: r.url }));
        } catch { /* anexo some da pré-visualização, não quebra a ficha */ }
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anexos.length, tipo]);

  const subir = async (file: File, categoria?: string) => {
    setSubindo(true);
    try {
      const pre = await api('op_estacao_ficha_anexo_url', {
        id: estacaoId, tipo, nome: file.name, content_type: file.type || 'application/octet-stream',
      });
      const put = await fetch(pre.upload_url, {
        method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      // `fetch` NÃO lança em 4xx/5xx. Sem este check, um PUT recusado (presigned
      // vencido, tipo divergente) seguia direto pro registro e deixava um anexo
      // no JSONB apontando pra objeto que nunca existiu — some da tela só como
      // "carregando…" eterno, e some do PDF sem explicação.
      if (!put.ok) throw new Error(`o S3 recusou o arquivo (HTTP ${put.status})`);
      await api('op_estacao_ficha_anexo_registrar', {
        id: estacaoId, tipo, s3_key: pre.s3_key, nome: file.name,
        content_type: file.type, tamanho_bytes: file.size, categoria: categoria || null,
      });
      message.success(`${file.name} anexado`);
      if (categorias) {
        const usadas = new Set(meus.map(m => m.categoria).concat(categoria || ''));
        setCategoria(categorias.find(c => !usadas.has(c.value))?.value);
      }
      recarregar();
    } catch (e: any) {
      message.error('Falha no upload: ' + (e.response?.data?.error || e.message));
    } finally { setSubindo(false); }
  };

  const remover = async (s3_key: string) => {
    try {
      await api('op_estacao_ficha_anexo_remover', { id: estacaoId, s3_key });
      message.success('Anexo removido'); recarregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  };

  const ehImagem = (a: any) => /^image\//.test(a.content_type || '') || /\.(jpe?g|png|gif|webp)$/i.test(a.nome || '');

  return (
    <div style={{ marginBottom: 16 }}>
      <Space style={{ marginBottom: 8 }} wrap>
        <Text strong>{titulo}</Text>
        {dica && <Text type="secondary" style={{ fontSize: 12 }}>{dica}</Text>}
        {!leitura && categorias && (
          <Select size="small" style={{ minWidth: 250 }} allowClear placeholder="Categoria da foto (ANA)"
            value={categoria} onChange={setCategoria} options={categorias.map(c => ({
              ...c, label: `${c.label}${meus.some(m => m.categoria === c.value) ? ' ✓' : ''}` }))} />
        )}
        {!leitura && (
          <Upload accept={aceita} showUploadList={false}
            beforeUpload={f => { subir(f as File, categoria); return false; }}>
            <Button size="small" icon={<UploadOutlined />} loading={subindo}>Anexar</Button>
          </Upload>
        )}
      </Space>
      {meus.length === 0
        ? <div><Text type="secondary" style={{ fontSize: 12 }}>Nenhum arquivo.</Text></div>
        : (
          <Row gutter={[12, 12]}>
            {meus.map(a => (
              <Col key={a.s3_key} xs={12} sm={8} md={6} xl={4}>
                <Card size="small" styles={{ body: { padding: 8 } }}>
                  <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', marginBottom: 6, overflow: 'hidden' }}>
                    {ehImagem(a) && urls[a.s3_key]
                      ? <Image src={urls[a.s3_key]} height={110} style={{ objectFit: 'cover' }} />
                      : <Text type="secondary" style={{ fontSize: 11 }}>{ehImagem(a) ? 'carregando…' : 'arquivo'}</Text>}
                  </div>
                  <Tooltip title={a.nome}>
                    <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.nome}</div>
                  </Tooltip>
                  {a.categoria && (
                    <Tag color="blue" style={{ fontSize: 10, marginTop: 4, whiteSpace: 'normal' }}>
                      {(categorias || []).find(c => c.value === a.categoria)?.label || a.categoria}
                    </Tag>
                  )}
                  <Space size={4} style={{ marginTop: 4 }}>
                    {/* `disabled={false}` explícito: o <Form disabled> da ficha em
                        modo leitura desliga TODO Button descendente via
                        ConfigProvider — inclusive o de baixar, que tem que
                        continuar funcionando pra quem só tem `ficha.ver`. */}
                    <Button size="small" type="text" icon={<DownloadOutlined />} disabled={false}
                      onClick={() => urls[a.s3_key] && window.open(urls[a.s3_key], '_blank')} />
                    {!leitura && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remover(a.s3_key)} />}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
    </div>
  );
}

// ── Importar ficha antiga (PDF) por IA ──────────────────────────────────────
// A Hydrodata tem fichas em PDF anteriores ao sistema. Redigitar 60 campos por
// estação é o tipo de trabalho que ninguém faz — e sem isso a ficha nova
// nasceria vazia. A IA lê e PROPÕE; nada entra no banco sem alguém conferir.
const ROTULOS_IA: Record<string, string> = {
  entidade_proprietaria: 'Entidade proprietária', codigo_entidade: 'Código na entidade',
  entidade_operadora: 'Entidade operadora', codigo_flu: 'Código FLU', codigo_plu: 'Código PLU',
  curso_dagua: "Curso d'água", bacia_hidrografica: 'Bacia', area_drenagem_km2: 'Área de drenagem (km²)',
  municipio: 'Município', uf: 'UF', localizacao: 'Localização', acessibilidade: 'Acessibilidade',
  infraestrutura: 'Infraestrutura', potamografia: 'Potamografia',
  reguas_num_lances: 'Réguas — nº de lances', reguas_lances_instalados: 'Réguas — instalados',
  reguas_margem: 'Réguas — margem', reguas_amplitude_m: 'Réguas — amplitude (m)',
  reguas_altitude_zero_m: 'Réguas — altitude do zero (m)', reguas_descricao_lances: 'Réguas — descrição',
  transbordamento_margem: 'Transbordamento — margem', transbordamento_cota_m: 'Transbordamento — cota (m)',
  medicao_distancia_reguas_m: 'Medição — distância às réguas (m)', medicao_localizacao: 'Medição — posição',
  medicao_tipo_travessia: 'Medição — travessia', medicao_distancia_pi_pf_m: 'Medição — PI–PF (m)',
  medicao_natureza_leito: 'Natureza do leito', medicao_processos: 'Processos de medição',
  meses_operacao: 'Meses de operação', trecho_regime: 'Trecho — regime',
  trecho_conformacao: 'Trecho — conformação', trecho_fundo: 'Trecho — fundo',
  controle_tipo: 'Controle a jusante', controle_distancia_m: 'Controle — distância (m)',
  estacao_montante_codigo: 'Estação a montante', estacao_montante_entidade: 'Entidade (montante)',
  estacao_jusante_codigo: 'Estação a jusante', estacao_jusante_entidade: 'Entidade (jusante)',
  tipos_monitoramento: 'Tipos de monitoramento', coordenadas: 'Coordenadas',
  equipamentos: 'Equipamentos', referencias_nivel: 'Referências de nível', margens: 'Margens',
  observacoes: 'Observações',
};

function ModalImportarIa({ estacaoId, onAplicar, onClose }: {
  estacaoId: string;
  onAplicar: (campos: Record<string, any>) => void;
  onClose: () => void;
}) {
  const [etapa, setEtapa] = useState<'subir' | 'lendo' | 'conferir'>('subir');
  const [campos, setCampos] = useState<Record<string, any> | null>(null);
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());

  const processar = async (file: File) => {
    setEtapa('lendo');
    try {
      const pre = await api('op_estacao_ficha_anexo_url', {
        id: estacaoId, tipo: 'ficha_pdf', nome: file.name, content_type: file.type || 'application/pdf',
      });
      const put = await fetch(pre.upload_url, {
        method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/pdf' },
      });
      // fetch NÃO lança em 4xx/5xx — sem este check o passo seguinte leria um
      // objeto que nunca chegou ao S3 e o erro sairia como "arquivo não encontrado".
      if (!put.ok) throw new Error(`o S3 recusou o arquivo (HTTP ${put.status})`);
      // Guarda o PDF antigo como anexo: é a procedência do que a IA leu.
      await api('op_estacao_ficha_anexo_registrar', {
        id: estacaoId, tipo: 'ficha_pdf', s3_key: pre.s3_key, nome: file.name,
        content_type: file.type, tamanho_bytes: file.size, origem: 'ficha_preterita',
      });
      const r = await api('op_estacao_ficha_importar_ia', { id: estacaoId, s3_key: pre.s3_key });
      const c = r.campos || {};
      setCampos(c);
      setEscolhidos(new Set(Object.keys(c)));
      setEtapa('conferir');
    } catch (e: any) {
      message.error('Não deu pra ler: ' + (e.response?.data?.error || e.message));
      setEtapa('subir');
    }
  };

  const chaves = campos ? Object.keys(campos).filter(k => ROTULOS_IA[k]) : [];
  const mostrar = (v: any) => {
    if (Array.isArray(v)) {
      return typeof v[0] === 'object'
        ? `${v.length} linha(s)`
        : v.join(', ');
    }
    return String(v);
  };

  return (
    <Modal open onCancel={onClose} width="min(900px, 94vw)"
      title="Importar de uma ficha antiga (PDF)"
      footer={etapa === 'conferir' ? [
        <Button key="c" onClick={onClose}>Cancelar</Button>,
        <Button key="a" type="primary" disabled={escolhidos.size === 0}
          onClick={() => {
            const sel: Record<string, any> = {};
            // Array.from e nao `for..of` no Set: o target do tsconfig e < es2015
            // e iterar Set direto nao compila (TS2802).
            Array.from(escolhidos).forEach(k => { sel[k] = campos![k]; });
            onAplicar(sel);
            onClose();
          }}>Preencher {escolhidos.size} campo(s) no formulário</Button>,
      ] : null}>
      {etapa === 'subir' && (
        <>
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="A leitura automática erra número"
            description="Ela serve pra não redigitar 60 campos. Confira cota, altitude e distância
                         antes de salvar — número trocado numa cota de régua é erro de engenharia,
                         não de digitação. Nada é gravado sem você clicar em Salvar ficha." />
          <Upload accept=".pdf" showUploadList={false}
            beforeUpload={f => { processar(f as File); return false; }}>
            <Button icon={<UploadOutlined />} disabled={false}>Escolher o PDF da ficha antiga</Button>
          </Upload>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Até ~4 MB. Se a ficha vier num PDF grande, mande só as páginas dela.
              O arquivo fica anexado como procedência do que foi lido.
            </Text>
          </div>
        </>
      )}
      {etapa === 'lendo' && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin /><div style={{ marginTop: 12 }}><Text type="secondary">Lendo a ficha…</Text></div>
        </div>
      )}
      {etapa === 'conferir' && (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={`${chaves.length} campo(s) encontrados. Desmarque o que não quiser trazer.`}
            description="Isto só PREENCHE o formulário — a ficha só é gravada quando você clicar em Salvar ficha." />
          {chaves.length === 0
            ? <Empty description="A leitura não achou campo nenhum reconhecível." />
            : (
              <Table size="small" pagination={false} rowKey="k" scroll={{ y: '50vh' }}
                dataSource={chaves.map(k => ({ k, rotulo: ROTULOS_IA[k], valor: campos![k] }))}
                columns={[
                  {
                    title: '', key: 'x', width: 40,
                    render: (_: any, r: any) => (
                      <Checkbox checked={escolhidos.has(r.k)} onChange={e => {
                        const n = new Set(Array.from(escolhidos));
                        if (e.target.checked) n.add(r.k); else n.delete(r.k);
                        setEscolhidos(n);
                      }} />
                    ),
                  },
                  { title: 'Campo', dataIndex: 'rotulo', key: 'r', width: 260 },
                  { title: 'Lido da ficha', key: 'v', render: (_: any, r: any) => <Text>{mostrar(r.valor)}</Text> },
                ]} />
            )}
        </>
      )}
    </Modal>
  );
}

// ── A ficha ──────────────────────────────────────────────────────────────────
export function TabFicha({ estacaoId, tipos, podeEditar, podePublicar }: {
  estacaoId: string;
  tipos: string[];
  podeEditar: boolean;
  podePublicar: boolean;
}) {
  const [form] = Form.useForm();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [pdfPronto, setPdfPronto] = useState<{ url: string; nome: string } | null>(null);
  const [importarOpen, setImportarOpen] = useState(false);
  // Se a carga falhou, `tabelas` continua nos 5 arrays VAZIOS — e salvar
  // mandaria `[]` pras 5 colunas JSONB, apagando no banco o que a tela nunca
  // chegou a mostrar. Sem carga bem-sucedida, não se salva.
  const [carregou, setCarregou] = useState(false);
  const [ficha, setFicha] = useState<any>(null);
  const [tabelas, setTabelas] = useState<Record<string, any[]>>({
    tipos_monitoramento: [], coordenadas: [], equipamentos: [], referencias_nivel: [], margens: [],
  });
  const leitura = !podeEditar;

  // Seções de régua/medição/controle só valem onde há régua: fluviométrica e
  // linimétrica. Pluviométrica pura não vê nada disso.
  const ehFluvial = tipos.some(t => t === 'fluviometrica' || t === 'linimetrica');

  // `soAnexos` existe pro fluxo de anexo: atualiza a lista de arquivos sem
  // tocar no formulário. Sem isso, anexar uma foto descartava a digitação.
  const carregar = useCallback(async (soAnexos = false) => {
    if (!soAnexos) setCarregando(true);
    try {
      const r = await api('op_estacao_ficha', { id: estacaoId });
      if (soAnexos) { setFicha((f: any) => ({ ...(f || {}), anexos: r.anexos || [] })); return; }
      setFicha(r);
      setCarregou(true);
      setTabelas({
        tipos_monitoramento: r.tipos_monitoramento || [], coordenadas: r.coordenadas || [],
        equipamentos: r.equipamentos || [], referencias_nivel: r.referencias_nivel || [],
        margens: r.margens || [],
      });
      form.setFieldsValue({
        ...r,
        // Pré-preenchimento a partir do cadastro quando a ficha ainda não existe
        // — o cadastro já sabe o corpo hídrico, não faz sentido redigitar.
        curso_dagua: r.curso_dagua || r.corpo_hidrico || undefined,
        croqui_data: r.croqui_data ? dayjs(r.croqui_data) : null,
        satelite_data: r.satelite_data ? dayjs(r.satelite_data) : null,
        fotos_data: r.fotos_data ? dayjs(r.fotos_data) : null,
      });
    } catch (e: any) {
      message.error('Erro ao carregar a ficha: ' + (e.response?.data?.error || e.message));
    } finally { if (!soAnexos) setCarregando(false); }
  }, [estacaoId, form]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (): Promise<boolean> => {
    if (!carregou) {
      message.error('A ficha não chegou a carregar. Recarregue a página antes de salvar.');
      return false;
    }
    setSalvando(true);
    try {
      const v = await form.validateFields();
      const corpo: Record<string, any> = { id: estacaoId, ...tabelas };
      for (const [k, val] of Object.entries(v)) {
        // Select de modo único com allowClear emite `undefined` ao ser limpo, e
        // JSON.stringify DESCARTA chave com undefined — o backend então nunca
        // via o campo e mantinha o valor antigo: a tela dizia "salvo" e o banco
        // não mudava. Aqui `undefined` vira `null` explícito = apagar de fato.
        corpo[k] = val === undefined ? null : val;
      }
      // As 3 datas ficam no painel de anexos, que o Collapse só monta quando
      // aberto. Enviá-las sempre (com `dt(undefined) === null`) APAGAVA a data
      // gravada de quem salvou sem abrir esse painel. Só vão se existirem.
      for (const k of ['croqui_data', 'satelite_data', 'fotos_data']) {
        if (k in v) corpo[k] = v[k] ? dayjs(v[k]).format('YYYY-MM-DD') : null;
      }
      await api('op_estacao_ficha_salvar', corpo);
      message.success('Ficha salva');
      await carregar();
      return true;
    } catch (e: any) {
      if (e?.errorFields) {
        message.error('Confira os campos destacados antes de salvar.');
        return false;
      }
      message.error('Erro ao salvar: ' + (e.response?.data?.error || e.message));
      return false;
    } finally { setSalvando(false); }
  };

  // O PDF é montado no servidor a partir do que ESTÁ GRAVADO — salvar antes
  // evita o clássico "gerei, e o PDF veio sem o que acabei de digitar".
  const gerarPdf = async () => {
    setGerandoPdf(true);
    try {
      // `salvar()` devolve false em erro de validação e em erro do backend. Sem
      // olhar o retorno, o PDF saía do que ESTAVA GRAVADO e o comentário
      // "salvar antes" virava mentira — pior: o usuário via o PDF antigo.
      if (podeEditar && !(await salvar())) return;
      const r = await api('op_estacao_ficha_pdf', { id: estacaoId });
      // Depois de ~8 s de espera o navegador já revogou a ativação transitória
      // do clique e o window.open volta null, silenciosamente. Guardar a URL e
      // oferecer um botão devolve o clique fresco que o navegador exige.
      const w = window.open(r.url, '_blank');
      if (!w) setPdfPronto({ url: r.url, nome: r.arquivo });
      if (r.anexos_pulados) {
        message.warning(`${r.anexos_pulados} anexo(s) ficaram fora do PDF (limite de tamanho/quantidade).`);
      }
      await carregar(true);
    } catch (e: any) {
      // O API Gateway corta em 29 s, mas o pdf-generator continua e SALVA o PDF
      // em anexos. Recarregar aqui faz o arquivo aparecer em "Fichas em PDF"
      // mesmo quando a espera estourou — sem isso o usuário geraria de novo.
      message.error('Não deu pra abrir a ficha agora: '
        + (e.response?.data?.error || e.message)
        + '. Se demorou, confira em "Fichas em PDF" logo abaixo.');
      carregar(true);
    } finally { setGerandoPdf(false); }
  };

  // A IA devolve campos planos e as 5 tabelas JSONB de uma vez. Os planos vão
  // pro Form; as tabelas, pro useState que as guarda. Só preenche — não salva.
  const aplicarImportado = (c: Record<string, any>) => {
    const tabelasIa: Record<string, any[]> = {};
    const planos: Record<string, any> = {};
    for (const [k, v] of Object.entries(c)) {
      if (k in tabelas) tabelasIa[k] = Array.isArray(v) ? v : [];
      else planos[k] = v;
    }
    if (Object.keys(tabelasIa).length) setTabelas(t => ({ ...t, ...tabelasIa }));
    form.setFieldsValue(planos);
    message.success(`${Object.keys(c).length} campo(s) preenchidos. Confira e clique em Salvar ficha.`);
  };

  if (carregando) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  const secoes: any[] = [];
  const item = (key: string, label: string, children: React.ReactNode) => secoes.push({ key, label, children });

  // ── 1. Identificação ──
  item('id', 'Identificação', (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={8}><Form.Item name="entidade_proprietaria" label="Entidade proprietária"><Input /></Form.Item></Col>
      <Col xs={12} md={4}><Form.Item name="codigo_entidade" label="Código na entidade"><Input /></Form.Item></Col>
      <Col xs={12} md={6}><Form.Item name="cnpj_empresa" label="CNPJ"><Input /></Form.Item></Col>
      <Col xs={24} md={6}><Form.Item name="entidade_operadora" label="Entidade operadora"><Input /></Form.Item></Col>
      <Col xs={12} md={4}><Form.Item name="codigo_flu" label="Código FLU (ANA)"
        tooltip="8 dígitos do inventário da ANA. É a chave de troca com o SRDH."><Input /></Form.Item></Col>
      <Col xs={12} md={4}><Form.Item name="codigo_plu" label="Código PLU (ANA)"><Input /></Form.Item></Col>
      <Col xs={24} md={8}><Form.Item name="curso_dagua" label="Curso d'água"><Input /></Form.Item></Col>
      <Col xs={24} md={8}><Form.Item name="bacia_hidrografica" label="Bacia hidrográfica"><Input /></Form.Item></Col>
      <Col xs={12} md={6}><Form.Item name="area_drenagem_km2" label="Área de drenagem (km²)"><InputNumber {...numProps} /></Form.Item></Col>
      <Col xs={12} md={10}><Form.Item name="municipio" label="Município"><Input /></Form.Item></Col>
      <Col xs={12} md={4}><Form.Item name="uf" label="UF"><Input maxLength={2} /></Form.Item></Col>
      <Col span={24}><Form.Item name="roteiro" label="Roteiro de acesso (resumo)"><Input /></Form.Item></Col>
    </Row>
  ));

  // ── 2. Tabelas do Anexo I ──
  item('monit', 'Tipos de monitoramento', (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 10 }}
        message="Uma linha por tipo monitorado. O 'método de obtenção' é o equipamento (pluviômetro, régua, sensor de pressão…); a 'forma de transmissão' é como o dado chega (satélite, celular, boletim, datalogger)." />
      <TabelaJson leitura={leitura} rotuloAdd="Adicionar tipo" valor={tabelas.tipos_monitoramento}
        onChange={v => setTabelas(t => ({ ...t, tipos_monitoramento: v }))}
        colunas={[
          { campo: 'tipo', titulo: 'Tipo', tipo: 'select', opcoes: ['Fluviométrico', 'Pluviométrico', 'Linimétrico', 'Sedimentométrico', 'Qualidade da água', 'Climatológico'] },
          { campo: 'data_instalacao', titulo: 'Instalação', tipo: 'data', largura: 130 },
          { campo: 'data_desativacao', titulo: 'Desativação', tipo: 'data', largura: 130 },
          { campo: 'metodo_obtencao', titulo: 'Método de obtenção' },
          { campo: 'forma_transmissao', titulo: 'Transmissão', tipo: 'select', opcoes: ['Satélite', 'Celular', 'Boletim', 'Datalogger'] },
        ]} />
    </>
  ));

  item('coord', 'Coordenadas', (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 10 }}
        message="A ANA pede a coordenada SOBRE o equipamento de cada tipo, não uma só da estação: o pluviômetro sobre o próprio, as réguas sobre uma RN, o sensor sobre o sensor." />
      <TabelaJson leitura={leitura} rotuloAdd="Adicionar coordenada" valor={tabelas.coordenadas}
        onChange={v => setTabelas(t => ({ ...t, coordenadas: v }))}
        colunas={[
          { campo: 'tipo', titulo: 'Referente a' },
          { campo: 'lat_grau', titulo: 'Lat °', tipo: 'num', largura: 80 },
          { campo: 'lat_min', titulo: 'Lat ′', tipo: 'num', largura: 80 },
          { campo: 'lat_seg', titulo: 'Lat ″', tipo: 'num', largura: 80 },
          { campo: 'lon_grau', titulo: 'Lon °', tipo: 'num', largura: 80 },
          { campo: 'lon_min', titulo: 'Lon ′', tipo: 'num', largura: 80 },
          { campo: 'lon_seg', titulo: 'Lon ″', tipo: 'num', largura: 80 },
          { campo: 'altitude_m', titulo: 'Altitude (m)', tipo: 'num', largura: 110 },
        ]} />
    </>
  ));

  item('equip', 'Equipamentos', (
    <TabelaJson leitura={leitura} rotuloAdd="Adicionar equipamento" valor={tabelas.equipamentos}
      onChange={v => setTabelas(t => ({ ...t, equipamentos: v }))}
      colunas={[
        { campo: 'equipamento', titulo: 'Equipamento' },
        { campo: 'marca', titulo: 'Marca' }, { campo: 'modelo', titulo: 'Modelo' },
        { campo: 'autonomia', titulo: 'Autonomia' },
        { campo: 'ativado_em', titulo: 'Ativado em', tipo: 'data', largura: 130 },
        { campo: 'desativado_em', titulo: 'Desativado em', tipo: 'data', largura: 130 },
      ]} />
  ));

  // ── 3. Descrição do local ──
  item('local', 'Localização, acesso e infraestrutura', (
    <Row gutter={[12, 0]}>
      <Col xs={24} lg={12}><Form.Item name="localizacao" label="Localização"
        tooltip="Margem, montante/jusante de barramentos e pontes, distâncias a referências."><TextArea rows={4} /></Form.Item></Col>
      <Col xs={24} lg={12}><Form.Item name="acessibilidade" label="Acessibilidade"
        tooltip="Roteiro passo a passo até a instalação — é o que a equipe de campo lê antes de sair."><TextArea rows={4} /></Form.Item></Col>
      <Col xs={24} lg={12}><Form.Item name="infraestrutura" label="Infraestrutura"><TextArea rows={3} /></Form.Item></Col>
      <Col xs={24} lg={12}><Form.Item name="potamografia" label="Potamografia"
        tooltip="Curso d'água, afluentes principais e municípios sob influência."><TextArea rows={3} /></Form.Item></Col>
    </Row>
  ));

  // ── 4..7. Só onde há régua ──
  if (ehFluvial) {
    item('reguas', 'Réguas linimétricas', (
      <Row gutter={[12, 0]}>
        <Col xs={12} md={5}><Form.Item name="reguas_num_lances" label="Nº de lances"><InputNumber {...numProps} precision={0} /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item name="reguas_lances_instalados" label="Lances instalados"><InputNumber {...numProps} precision={0} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item name="reguas_margem" label="Margem">
          <Select allowClear options={OPTS('esquerda', 'direita')} /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item name="reguas_amplitude_m" label="Amplitude (m)"><InputNumber {...numProps} /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item name="reguas_altitude_zero_m" label="Altitude do zero (m)"><InputNumber {...numProps} /></Form.Item></Col>
        <Col span={24}><Form.Item name="reguas_descricao_lances" label="Descrição dos lances"><TextArea rows={3} /></Form.Item></Col>
      </Row>
    ));

    item('rns', 'Referências de nível (RNs)', (
      <>
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message="Altitude da RN = altitude do zero da régua + cota da RN." />
        <TabelaJson leitura={leitura} rotuloAdd="Adicionar RN" valor={tabelas.referencias_nivel}
          onChange={v => setTabelas(t => ({ ...t, referencias_nivel: v }))}
          colunas={[
            { campo: 'rn', titulo: 'RN', largura: 90 },
            { campo: 'cota_mm', titulo: 'Cota (mm)', tipo: 'num', largura: 110 },
            { campo: 'altitude_m', titulo: 'Altitude (m)', tipo: 'num', largura: 120 },
            { campo: 'estabilidade', titulo: 'Estabilidade', tipo: 'select', opcoes: ['Estável', 'Instável', 'Destruída'] },
            { campo: 'descricao', titulo: 'Descrição' },
          ]} />
      </>
    ));

    item('transb', 'Cota de transbordamento', (
      <Row gutter={[12, 0]}>
        <Col xs={12} md={6}><Form.Item name="transbordamento_margem" label="Margem">
          <Select allowClear options={OPTS('esquerda', 'direita', 'ambas')} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="transbordamento_cota_m" label="Cota (m)"><InputNumber {...numProps} /></Form.Item></Col>
      </Row>
    ));

    item('medicao', 'Seção de medição de descarga', (
      <Row gutter={[12, 0]}>
        <Col xs={12} md={6}><Form.Item name="medicao_distancia_reguas_m" label="Distância às réguas (m)"><InputNumber {...numProps} /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item name="medicao_localizacao" label="Em relação às réguas">
          <Select allowClear options={OPTS('montante', 'jusante')} /></Form.Item></Col>
        <Col xs={12} md={7}><Form.Item name="medicao_tipo_travessia" label="Tipo de travessia">
          <Select allowClear showSearch options={OPTS('Vau', 'Ponte', 'Barco', 'Teleférico', 'Passarela', 'Guincho')} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="medicao_distancia_pi_pf_m" label="Distância PI–PF (m)"><InputNumber {...numProps} /></Form.Item></Col>
        <Col xs={24} md={12}><Form.Item name="medicao_natureza_leito" label="Natureza do leito">
          <Select mode="tags" allowClear placeholder="areia, cascalho, rocha…"
            options={OPTS('Areia', 'Silte', 'Argila', 'Cascalho', 'Pedregulho', 'Matacão', 'Rocha', 'Vegetação')} /></Form.Item></Col>
        <Col xs={24} md={12}><Form.Item name="medicao_processos" label="Processos de medição">
          <Select mode="tags" allowClear placeholder="a vau, ADCP, molinete…"
            options={OPTS('A vau', 'Molinete', 'ADCP', 'Flutuador', 'Barco a motor', 'Teleférico')} /></Form.Item></Col>
        <Col span={24}><Form.Item name="meses_operacao" label="Meses de operação"
          tooltip="A ANA pede no mínimo 4 medições por ano, em intervalos trimestrais.">
          <Checkbox.Group>
            <Row gutter={[8, 4]}>
              {MESES.map((m, i) => <Col key={m} xs={6} sm={4} md={2}><Checkbox value={i + 1}>{m}</Checkbox></Col>)}
            </Row>
          </Checkbox.Group>
        </Form.Item></Col>
      </Row>
    ));

    item('trecho', 'Características do trecho, margens e controle', (
      <>
        <Row gutter={[12, 0]}>
          <Col xs={12} md={5}><Form.Item name="trecho_regime" label="Regime">
            <Select allowClear options={OPTS('perene', 'intermitente', 'efêmero')} /></Form.Item></Col>
          <Col xs={12} md={6}><Form.Item name="trecho_conformacao" label="Conformação">
            <Select allowClear options={OPTS('retilíneo', 'anastomosado', 'meandrante', 'curvo')} /></Form.Item></Col>
          <Col xs={12} md={4}><Form.Item name="trecho_fundo" label="Fundo">
            <Select allowClear options={OPTS('regular', 'irregular')} /></Form.Item></Col>
          <Col xs={12} md={5}><Form.Item name="controle_tipo" label="Controle a jusante">
            <Select allowClear showSearch options={OPTS('Corredeira', 'Cachoeira', 'Ponte', 'Bueiro', 'Soleira', 'Estreitamento', 'Nenhum')} /></Form.Item></Col>
          <Col xs={12} md={4}><Form.Item name="controle_distancia_m" label="Distância (m)"><InputNumber {...numProps} /></Form.Item></Col>
        </Row>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Margens</Text>
        <TabelaJson leitura={leitura} rotuloAdd="Adicionar margem" valor={tabelas.margens}
          onChange={v => setTabelas(t => ({ ...t, margens: v }))}
          colunas={[
            { campo: 'margem', titulo: 'Margem', tipo: 'select', opcoes: ['esquerda', 'direita'], largura: 130 },
            { campo: 'natureza', titulo: 'Natureza' },
            { campo: 'vegetacao', titulo: 'Vegetação' },
            { campo: 'inclinacao', titulo: 'Inclinação' },
          ]} />
      </>
    ));
  }

  // ── 8. Rede ──
  item('rede', 'Posição na rede hidrológica', (
    <Row gutter={[12, 0]}>
      <Col xs={12} md={6}><Form.Item name="estacao_montante_codigo" label="Estação a montante"><Input /></Form.Item></Col>
      <Col xs={12} md={6}><Form.Item name="estacao_montante_entidade" label="Entidade (montante)"><Input /></Form.Item></Col>
      <Col xs={12} md={6}><Form.Item name="estacao_jusante_codigo" label="Estação a jusante"><Input /></Form.Item></Col>
      <Col xs={12} md={6}><Form.Item name="estacao_jusante_entidade" label="Entidade (jusante)"><Input /></Form.Item></Col>
    </Row>
  ));

  // ── 9. Anexos ──
  item('anexos', `Croqui, satélite e fotos${ficha?.anexos?.length ? ` (${ficha.anexos.length})` : ''}`, (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={12} md={6}><Form.Item name="croqui_responsavel" label="Croqui — responsável"><Input /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item name="croqui_data" label="Croqui — data"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="fotos_responsavel" label="Fotos — responsável"><Input /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item name="fotos_data" label="Fotos — data"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item name="satelite_data" label="Satélite — data"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col span={24}><Form.Item name="satelite_observacoes" label="Satélite — observações"><Input /></Form.Item></Col>
      </Row>
      <BlocoAnexos estacaoId={estacaoId} tipo="croqui_localizacao" titulo="Croqui de localização"
        anexos={ficha?.anexos || []} recarregar={() => carregar(true)} leitura={leitura} aceita="image/*,.pdf" />
      <BlocoAnexos estacaoId={estacaoId} tipo="croqui_acesso" titulo="Croqui de acesso"
        anexos={ficha?.anexos || []} recarregar={() => carregar(true)} leitura={leitura} aceita="image/*,.pdf" />
      <BlocoAnexos estacaoId={estacaoId} tipo="imagem_satelite" titulo="Imagem de satélite"
        anexos={ficha?.anexos || []} recarregar={() => carregar(true)} leitura={leitura} aceita="image/*" />
      <BlocoAnexos estacaoId={estacaoId} tipo="foto" titulo="Registro fotográfico"
        dica="o ✓ marca a categoria que já tem foto" categorias={CATEGORIAS_FOTO}
        anexos={ficha?.anexos || []} recarregar={() => carregar(true)} leitura={leitura} aceita="image/*" />
      <BlocoAnexos estacaoId={estacaoId} tipo="ficha_pdf" titulo="Fichas em PDF (geradas ou pretéritas)"
        anexos={ficha?.anexos || []} recarregar={() => carregar(true)} leitura={leitura} aceita=".pdf" />
    </>
  ));

  item('obs', 'Observações', (
    <Form.Item name="observacoes" noStyle><TextArea rows={4} /></Form.Item>
  ));

  return (
    <Form form={form} layout="vertical" size="small" disabled={leitura}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <Space wrap>
          <Title level={5} style={{ margin: 0 }}>Ficha descritiva</Title>
          <Tag color="blue">padrão ANA — Anexo I</Tag>
          {!ficha?.existe && <Tag>ainda não preenchida</Tag>}
          {ficha?.atualizado_em && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              atualizada em {dayjs(ficha.atualizado_em).format('DD/MM/YYYY HH:mm')}
              {ficha.atualizado_por ? ` por ${ficha.atualizado_por}` : ''}
            </Text>
          )}
        </Space>
        <Space wrap>
          {!leitura && (
            <Button icon={<UploadOutlined />} disabled={false} onClick={() => setImportarOpen(true)}>
              Importar de ficha antiga
            </Button>
          )}
          {podePublicar && (
            <Button icon={<FilePdfOutlined />} loading={gerandoPdf} disabled={false}
              onClick={gerarPdf} title={ficha?.existe ? undefined : 'Preencha e salve a ficha antes de gerar o PDF'}>
              Gerar PDF (padrão ANA)
            </Button>
          )}
          {!leitura && (
            <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={salvar} disabled={false}>
              Salvar ficha
            </Button>
          )}
        </Space>
      </div>
      {pdfPronto && (
        <Alert type="success" showIcon closable style={{ marginBottom: 12 }}
          onClose={() => setPdfPronto(null)}
          message="Ficha gerada"
          description={<span>
            O navegador bloqueou a abertura automática (a geração demora mais que o clique).{' '}
            <a href={pdfPronto.url} target="_blank" rel="noreferrer">Abrir {pdfPronto.nome}</a>
          </span>} />
      )}
      {!ehFluvial && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Estação sem régua: as seções de réguas, RNs, transbordamento, seção de medição e controle não aparecem." />
      )}
      <Collapse accordion={false} defaultActiveKey={['id']} items={secoes} />
      {importarOpen && (
        <ModalImportarIa estacaoId={estacaoId} onClose={() => setImportarOpen(false)}
          onAplicar={aplicarImportado} />
      )}
    </Form>
  );
}
